import { useSyncExternalStore } from "react";
import { useUserInfo } from "./userInfo";

export const RECHARGE_INTERVAL_MS = 15_000;
export class RechargeCooldownError extends Error {}

// One lock per account, shared by every recharge entry in this tab.
export function createRechargeCooldownStore(storage?: Storage) {
  const entries = new Map<string, { until: number; pending: boolean }>();
  const listeners = new Set<() => void>();
  let ticker: ReturnType<typeof setTimeout> | undefined;
  const scheduleTick = () => {
    clearTimeout(ticker);
    if (!listeners.size) return;
    const now = Date.now();
    const delays = Array.from(entries.values())
      .map(({ until }) => until - now)
      .filter((remaining) => remaining > 0)
      .map((remaining) => ((remaining - 1) % 1000) + 1);
    if (delays.length) ticker = setTimeout(notify, Math.min(...delays));
  };
  const notify = () => {
    listeners.forEach((listener) => listener());
    scheduleTick();
  };
  const getEntry = (account: string) => {
    let entry = entries.get(account);
    if (!entry) {
      let until = 0;
      try {
        until = Number(storage?.getItem(`recharge-cooldown:${account}`)) || 0;
      } catch (_) {
        // WebViews may disable storage; keep the in-memory lock working.
      }
      if (!Number.isFinite(until) || until > Date.now() + RECHARGE_INTERVAL_MS) until = 0;
      entry = { until, pending: false };
      entries.set(account, entry);
      scheduleTick();
    }
    return entry;
  };
  const isBlocked = (account: string) => {
    const entry = getEntry(account);
    return entry.pending || Date.now() < entry.until;
  };
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      window.addEventListener("focus", notify);
      scheduleTick();
      return () => {
        listeners.delete(listener);
        if (!listeners.size) {
          clearTimeout(ticker);
          ticker = undefined;
          window.removeEventListener("focus", notify);
        }
      };
    },
    isBlocked,
    remainingSeconds: (account: string) => Math.max(0, Math.ceil((getEntry(account).until - Date.now()) / 1000)),
    async run<T>(account: string, submit: () => Promise<T>): Promise<T> {
      if (isBlocked(account)) throw new RechargeCooldownError();
      const entry = getEntry(account);
      entry.until = Date.now() + RECHARGE_INTERVAL_MS;
      entry.pending = true;
      try {
        storage?.setItem(`recharge-cooldown:${account}`, String(entry.until));
      } catch (_) {
        // Storage failure must not prevent payment submission.
      }
      notify();
      try {
        return await submit();
      } finally {
        entry.pending = false;
        notify();
      }
    },
  };
}

let sessionStorage: Storage | undefined;
try { sessionStorage = window.sessionStorage; } catch (_) { /* storage unavailable */ }
const cooldown = createRechargeCooldownStore(sessionStorage);

export function useRechargeCooldown() {
  const { data: userInfo } = useUserInfo();
  const account = String(userInfo?.userId ?? "session");
  const isRechargeBlocked = useSyncExternalStore(
    cooldown.subscribe,
    () => cooldown.isBlocked(account),
  );
  const remainingSeconds = useSyncExternalStore(
    cooldown.subscribe,
    () => cooldown.remainingSeconds(account),
  );
  return {
    remainingSeconds,
    isRechargeBlocked,
    runRecharge: <T,>(submit: () => Promise<T>) => cooldown.run(account, submit),
  };
}
